import { PersonalityKey } from './NormalNpcData';

/**
 * 颁奖夜台词（批 B）：得主获奖感言 + 主持报幕模板 + 玩家获奖时的三种发言。
 * 设计=docs/新增功能-年度奖项与网友评价.md §6 批 B；审定稿=docs/颁奖夜文案填写表.md。
 *
 * 三条口径：
 *  - ⛔ 台词不掷骰：抽哪一条由「届次 + 按揭晓顺序的第几个」算出的稳定序号决定
 *    （seed = `awardId-edition`）。同一届刷新页面、关掉再开，念的都是同一句话——
 *    与微博「发布瞬间定格」「刷新不许跳」同规，只是这里靠可复算而不是靠落盘。
 *  - 占位符缺数据 ⇒ 那条整句跳过不抽（沿用评价库「取不到数据的文案不抽」口径）：
 *    开场剧情届得主没有角色定位与咖位，凡带 %角色名%/%咖位名% 的句子自动出局。
 *  - ⛔ 台词不改任何数值：获奖/提名的名气口碑在 AwardSystem.payOut 结算时已发完，
 *    玩家选哪一句只决定现场说什么（往屆舆论钩子留给批 C）。
 */

/** 玩家获奖感言的三种风格 */
export type SpeechTone = 'thanks' | 'self' | 'joke';
/** 咖位档（与评价库 §5.2 的咖位三档同切口）：Lv.1~4 起势 / Lv.5~7 有名 / Lv.8~10 顶层 */
export type RankBand = 'rising' | 'star' | 'top';

export const TONE_LABELS: Record<SpeechTone, string> = {
    thanks: '挨个道谢',
    self: '说说自己',
    joke: '打趣一句',
};

/** 得主获奖感言：按 NPC 性格分桶，4 性格 × 4 条 */
export const NPC_SPEECHES: Record<PersonalityKey, string[]> = {
    cheerful: [
        '哎哟这奖杯比我想象中沉。谢谢评审还惦记着我——我在《%作品名%》剧组笑了整整三个月。',
        '真没想到是我……那先笑一个给大家吧。谢谢到现在还没取关我的每一个人。',
        '我在台下想过无数种开场白，一站上来全忘了。那就一句：我还会一直演下去。',
        '这个角色救过我，是真的。拍《%作品名%》那阵子我天天把自己关在房间里。',
    ],
    quiet: [
        '我不太会讲话。%角色名%教会我的东西，都在戏里了。谢谢。',
        '拿了这个奖，说明那几年没白过。就这样吧，我回去拍戏了。',
        '谢谢评审。我只想说一句——请把灯光也给一给幕后的朋友们。',
        '入行十一年，站在这儿还是头一回。够了，再多说就要哭了。',
    ],
    sharp: [
        '这一行里，有人只看流量，有人还在看戏。谢谢%奖项名%的各位评审做了后者。',
        '我演%角色名%的时候没人看好。今天我把那句话还回去：别急着给一个演员定性。',
        '奖杯我收下。但我更想让台下听到——《%作品名%》的编剧名字，也该出现在这个台上。',
        '别问我感不感谢。我感谢摄影和灯光，是他们让我看起来像会演戏。',
    ],
    warm: [
        '谢谢导演肯等我把这个角色演慢一点。也记得剧组每一个岗位的名字。',
        '我把它转给《%作品名%》所有没走上过这个台的同事——你们和我一样好。',
        '我妈今天在看直播。妈，我说到做到了。',
        '演%角色名%那半年，我学会了先听别人把话讲完。这个奖属于听我讲话的人。',
    ],
};

/** 玩家获奖发言选项：3 风格 × 3 咖位档（小透明喊顶流台词会出戏） */
export const PLAYER_SPEECHES: Record<SpeechTone, Record<RankBand, string>> = {
    thanks: {
        rising: '谢谢评审，谢谢剧组里的每一个人。我入行没几年，今天先替他们高兴。',
        star: '谢谢。这个位置我在台下看了很多年，今天我站上来了——先谢谢一路带我的人。',
        top: '谢谢评审。我是被很多人托上来的，那些名字我记着，下了台一个一个去谢。',
    },
    self: {
        rising: '我不是这批人里最有天赋的，大概是最不肯下桌的那个。这顿饭我吃得踏实。',
        star: '有人劝我换条路走。我没换。今天算是给了自己一个交代。',
        top: '我能演到今天靠的不是运气，是一部一部挑出来的。这个奖我接得理所当然。',
    },
    joke: {
        rising: '我经纪人刚才在台下比我还紧张。回去让他抱一下，就一下。',
        star: '有人说这行的奖是给熬得住的人的。那我今天算熬出头了——明天还得接着拍。',
        top: '我助理早把下个月行程排满了。我先下去了，别耽误她开工。',
    },
};

/** 台词可填的字段；空串视为缺数据 ⇒ 含该占位符的句子整条跳过 */
export interface SpeechContext {
    awardName: string;
    edition: number;
    categoryName: string;
    winnerName: string;
    workName: string;
    roleName: string;
    rankName: string;
}

function fill(tpl: string, ctx: SpeechContext): string {
    return tpl
        .replace(/%奖项名%/g, ctx.awardName)
        .replace(/%作品名%/g, ctx.workName)
        .replace(/%角色名%/g, ctx.roleName)
        .replace(/%咖位名%/g, ctx.rankName);
}

/** 句子还填得出来吗（缺数据的占位符 ⇒ 整条出局） */
function fillable(tpl: string, ctx: SpeechContext): boolean {
    const need = (p: string, v: string) => tpl.includes(p) && !v;
    return !(need('%作品名%', ctx.workName) || need('%角色名%', ctx.roleName)
        || need('%咖位名%', ctx.rankName));
}

/** FNV-1a：把「届次」压成一个正整数种子（⛔ 用 Math.random，刷新就换词） */
function hashSeed(text: string): number {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return Math.abs(h);
}

export function rankBandOf(rank: number): RankBand {
    return rank >= 8 ? 'top' : rank >= 5 ? 'star' : 'rising';
}

/**
 * 剧情届得主（开场第 23 届那四位）在代码里没有 NPC 实体、也就没有性格模板 ⇒
 * 按名字稳定映射到一个性格。⛔ 用随机：同一届每次播出得是同一个口吻。
 */
export function fallbackPersonality(name: string): PersonalityKey {
    const keys = Object.keys(NPC_SPEECHES) as PersonalityKey[];
    return keys[hashSeed(name) % keys.length];
}

/**
 * 得主获奖感言：同一届内按揭晓顺序错开取词（4 条池 × 最多 4 人次 ⇒ 同届不撞句）。
 * ordinal = 该类别在本届的揭晓序号 0~3。
 */
export function winnerSpeech(personality: PersonalityKey, awardId: string, edition: number,
    ordinal: number, ctx: SpeechContext): string {
    const pool = NPC_SPEECHES[personality].filter(t => fillable(t, ctx));
    if (!pool.length) return '';
    const idx = (hashSeed(`${awardId}-${edition}`) + ordinal) % pool.length;
    return fill(pool[idx], ctx);
}

/**
 * 特殊 NPC 的专属获奖感言（NPCDef.awardSpeechText）。
 * 缺省、空白或占位符取不到值则返回空串，调用方回落四性格通用池。
 * 单条不参与届次抽词：同一届刷新仍是同一句。
 */
export function customWinnerSpeech(tpl: string | undefined, ctx: SpeechContext): string {
    const text = (tpl ?? '').trim();
    return text && fillable(text, ctx) ? fill(text, ctx) : '';
}

/** 玩家获奖时的三个选项（文案随咖位档换措辞） */
export function playerSpeechChoices(rank: number): { tone: SpeechTone; text: string }[] {
    const band = rankBandOf(rank);
    return (Object.keys(PLAYER_SPEECHES) as SpeechTone[])
        .map(tone => ({ tone, text: PLAYER_SPEECHES[tone][band] }));
}

export function playerSpeechText(tone: SpeechTone, rank: number): string {
    return PLAYER_SPEECHES[tone][rankBandOf(rank)];
}

// ===== 主持报幕：模板拼接，⛔ 占文案位（用户定稿：报幕是流程串场，不是创作） =====

/** 入围念名：只在玩家入围的类别念全名单，其余类别直接报得主 */
export function hostNominate(ctx: SpeechContext, nominees: { name: string; workName: string }[]): string {
    return `第 ${ctx.edition} 届 ${ctx.awardName}${ctx.categoryName}，入围的是——`
        + nominees.map(n => `${n.name}《${n.workName}》`).join('、') + '。';
}

export function hostWinner(ctx: SpeechContext): string {
    return `${ctx.awardName}${ctx.categoryName}，得主是——${ctx.winnerName}，《${ctx.workName}》。`;
}

export function hostVacant(ctx: SpeechContext): string {
    return `${ctx.awardName}${ctx.categoryName}，本届从缺——资格线内没有合格作品。`;
}

export function hostInvitePlayer(ctx: SpeechContext): string {
    // ⛔ 拿咖位标签当称呼（「恭喜。无人在意，上台说两句。」实机读出来像故障），按类别+姓名点人
    return `恭喜。请${ctx.categoryName}得主${ctx.winnerName}上台说两句。`;
}
